import React from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/seo/SEO";
import { LocalBusinessSchema } from "@/components/seo/LocalBusinessSchema";
import { ServiceSchema } from "@/components/seo/ServiceSchema";
import { BreadcrumbSchema } from "@/components/seo/BreadcrumbSchema";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Star, MapPin, Phone, Clock, Zap, Shield } from "lucide-react";
import { track } from "@/lib/analytics";

const LaadpaalDuiven = () => {
  const handleQuoteRequest = () => {
    track('quote_request', { service: 'laadpaal', location: 'duiven' });
    window.open('https://app.sol-vi.nl/form', '_blank');
  };

  const localBusinessData = {
    name: "SOL-VI Laadpaal Installatie Duiven",
    description: "Specialist in laadpaal installatie in Duiven. Erkend installateur voor thuis laadstations.",
    url: "https://sol-vi.nl/laadpaal-duiven",
    telephone: "+31 6 55 33 18 70",
    email: "info@sol-vi.nl",
    address: {
      streetAddress: "Hoofdstraat 1",
      addressLocality: "Elst",
      addressRegion: "Gelderland",
      postalCode: "6661 EA", 
      addressCountry: "NL"
    },
    openingHours: ["Mo-Fr 08:00-17:00"],
    priceRange: "€€",
    serviceArea: ["Duiven", "Elst", "Arnhem", "Westervoort", "Gelderland"],
    aggregateRating: {
      ratingValue: 4.9,
      reviewCount: 89
    }
  };

  const serviceData = {
    name: "Laadpaal Installatie Duiven", 
    description: "Professionele installatie van elektrische laadpalen in Duiven. Van advies tot installatie met slimme laadoplossingen.",
    provider: "SOL-VI",
    areaServed: ["Duiven", "Groessen", "Loo"],
    serviceType: "Laadpaal Installatie",
    url: "https://sol-vi.nl/laadpaal-duiven",
    offers: [
      {
        name: "Standaard Laadpaal 11kW",
        description: "Slimme laadpaal met app bediening voor thuisgebruik",
        priceRange: "€1.200 - €1.800"
      },
      {
        name: "Premium Laadpaal 22kW",
        description: "Snellaadpaal met dynamic load balancing",
        priceRange: "€1.800 - €2.500"
      }
    ]
  };

  const breadcrumbData = [
    { name: "Home", url: "https://sol-vi.nl" },
    { name: "Laadpalen", url: "https://sol-vi.nl/laadpalen" },
    { name: "Duiven", url: "https://sol-vi.nl/laadpaal-duiven" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Laadpaal Duiven | Thuis Laadstation Installatie | SOL-VI"
        description="🚗 Laadpaal installatie in Duiven ⚡ Erkend installateur 🏠 Slimme laadoplossingen ✅ Gratis advies en offerte. Bel: 06-55331870"
        keywords="laadpaal duiven, laadpaal installatie duiven, thuis laadstation duiven, elektrische auto laden duiven"
        canonical="https://sol-vi.nl/laadpaal-duiven"
        type="website"
      />
      
      <LocalBusinessSchema {...localBusinessData} />
      <ServiceSchema {...serviceData} />
      <BreadcrumbSchema items={breadcrumbData} />
      
      <Header />
      
      <main>
        {/* Hero Section */}
        <div className="pt-20 pb-12 bg-gradient-subtle">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="w-5 h-5 text-primary" />
                  <span className="text-sm font-medium text-primary">Duiven, Gelderland</span>
                </div>
                <h1 className="text-4xl md:text-5xl font-bold mb-6">
                  Laadpaal Installatie Duiven
                </h1>
                <p className="text-xl text-muted-foreground mb-8">
                  Slimme laadpaal voor thuis in Duiven. Erkend installateur met ervaring 
                  in elektrische laadstations. Gratis advies en snelle installatie.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button size="lg" onClick={handleQuoteRequest}>
                    Gratis Advies Duiven
                  </Button>
                  <div className="flex gap-2">
                    <Button variant="outline" size="lg" asChild>
                      <a href="tel:+31616929110">
                        <Phone className="w-4 h-4 mr-2" />
                        Sven: 06-16929110
                      </a>
                    </Button>
                    <Button variant="outline" size="lg" asChild>
                      <a href="tel:+31617105786">
                        <Phone className="w-4 h-4 mr-2" />
                        Nikki: 06-17105786
                      </a>
                    </Button>
                  </div>
                </div>
              </div>
              <div className="bg-card rounded-lg p-6 shadow-lg">
                <h3 className="text-lg font-semibold mb-4">Laadpaal Voordelen</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Zap className="w-5 h-5 text-primary" />
                    <span>Snel thuis opladen</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-primary" />
                    <span>Veilige installatie volgens norm</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Star className="w-5 h-5 text-yellow-500 fill-current" />
                    <span>4.9/5 sterren (89 reviews)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              Waarom Kiezen voor een Laadpaal in Duiven?
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <Card>
                <CardHeader>
                  <CardTitle>Kostenbesparing</CardTitle>
                  <CardDescription>
                    Laden thuis is tot 50% goedkoper dan openbaar laden
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Met thuistarieven bespaart u flink op laadkosten vergeleken met publieke laadpalen.
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Gemak & Comfort</CardTitle>
                  <CardDescription>
                    Start elke dag met een volledig opgeladen auto
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Geen zoeken naar vrije laadpalen - uw auto laadt 's nachts op terwijl u slaapt.
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Slimme Functies</CardTitle>
                  <CardDescription>
                    App bediening, timers en energiebeheer
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Beheer uw laadsessies via de app en optimaliseer het energieverbruik.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Installation Process */}
        <section className="py-16 bg-gradient-subtle">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              Hoe Verloopt de Installatie in Duiven?
            </h2>
            <div className="grid md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="bg-primary text-primary-foreground rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  1
                </div>
                <h3 className="font-semibold mb-2">Locatie Check</h3>
                <p className="text-sm text-muted-foreground">
                  Wij bekijken uw meterkast en parkeerplaats in Duiven
                </p>
              </div>
              
              <div className="text-center">
                <div className="bg-primary text-primary-foreground rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  2
                </div>
                <h3 className="font-semibold mb-2">Offerte op Maat</h3>
                <p className="text-sm text-muted-foreground">
                  Persoonlijk advies en transparante prijsopgave
                </p>
              </div>
              
              <div className="text-center">
                <div className="bg-primary text-primary-foreground rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  3
                </div>
                <h3 className="font-semibold mb-2">Vakkundige Installatie</h3>
                <p className="text-sm text-muted-foreground">
                  Gecertificeerde monteurs zorgen voor veilige installatie
                </p>
              </div>
              
              <div className="text-center">
                <div className="bg-primary text-primary-foreground rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                  4
                </div>
                <h3 className="font-semibold mb-2">Direct Gebruiken</h3>
                <p className="text-sm text-muted-foreground">
                  Na keuring en configuratie kunt u direct laden
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Local Projects */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              Laadpalen in Duiven
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold mb-2">Centrum Duiven</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    11kW laadpaal met app bediening
                  </p>
                  <p className="text-sm">
                    "Perfect geïnstalleerd, laden gaat supersnel en app werkt uitstekend!" 
                    - Familie Hermans
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold mb-2">Groessen</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    22kW laadpaal met zonnepanelen
                  </p>
                  <p className="text-sm">
                    "Geweldige combinatie met zonnepanelen - nu laden we gratis met de zon!" 
                    - Dhr. van Dijk
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold mb-2">Duiven-Noord</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Slimme laadpaal met dynamic load balancing
                  </p>
                  <p className="text-sm">
                    "Innovatieve oplossing, voorkomt overbelasting van de meterkast." 
                    - Mevr. Bakker
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-subtle">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-6">
              Klaar voor een Laadpaal in Duiven?
            </h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Ontdek welke laadpaal het beste bij uw situatie past. 
              Gratis advies en transparante prijzen.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" onClick={handleQuoteRequest}>
                Gratis Laadpaal Advies
              </Button>
              <Button variant="outline" size="lg" asChild>
                <a href="/contact">Contact Opnemen</a>
              </Button>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default LaadpaalDuiven;